import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, FormHelperText, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import CodeIcon from "@mui/icons-material/Code";

const cleanPastedHtml = (html) => {
    if (!html || typeof DOMParser === "undefined") return html || "";
    const doc = new DOMParser().parseFromString(html, "text/html");

    doc.querySelectorAll("script, style, meta, link, object, iframe").forEach((node) => node.remove());
    doc.body.querySelectorAll("*").forEach((node) => {
        [...node.attributes].forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            if (name.startsWith("on") || name === "id" || name === "class" || name.startsWith("data-") || name.startsWith("aria-")) {
                node.removeAttribute(attribute.name);
            }
        });

        if (node.hasAttribute("style")) {
            const allowed = ["font-weight", "font-style", "color", "background-color", "text-decoration", "text-align"];
            const styles = node.getAttribute("style").split(";").map((item) => item.trim()).filter(Boolean);
            const safeStyles = styles.filter((item) => allowed.includes(item.split(":")[0]?.trim().toLowerCase()));
            safeStyles.length ? node.setAttribute("style", safeStyles.join("; ")) : node.removeAttribute("style");
        }
    });

    return doc.body.innerHTML;
};

const HtmlEditor = forwardRef(function HtmlEditor(
    { label, value = "", onChange, helperText, minHeight = 120 },
    ref
) {
    const editorRef = useRef(null);
    const savedRangeRef = useRef(null);
    const [sourceMode, setSourceMode] = useState(false);

    useEffect(() => {
        if (!sourceMode && editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || "";
        }
    }, [sourceMode, value]);

    const emitValue = () => onChange?.(editorRef.current?.innerHTML || "");

    const rememberSelection = () => {
        const selection = window.getSelection();
        if (selection?.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        editorRef.current?.focus();
        if (!savedRangeRef.current) return;
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
    };

    const applyInlineStyle = (styles) => {
        restoreSelection();
        const selection = window.getSelection();
        if (!selection?.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        if (!editorRef.current?.contains(range.commonAncestorContainer)) return;

        const wrapper = document.createElement("span");
        Object.assign(wrapper.style, styles);
        wrapper.appendChild(range.extractContents());
        range.insertNode(wrapper);

        range.selectNodeContents(wrapper);
        selection.removeAllRanges();
        selection.addRange(range);
        savedRangeRef.current = range.cloneRange();
        emitValue();
    };

    const insertHtml = (html) => {
        if (sourceMode) {
            onChange?.(`${value || ""}${html}`);
            return;
        }
        restoreSelection();
        document.execCommand("insertHTML", false, html);
        emitValue();
    };

    useImperativeHandle(ref, () => ({ insertHtml }));

    const handlePaste = (event) => {
        event.preventDefault();
        const clipboard = event.clipboardData;
        const html = clipboard.getData("text/html");
        const plainText = clipboard.getData("text/plain");
        const content = html
            ? cleanPastedHtml(html)
            : plainText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r?\n/g, "<br>");
        document.execCommand("insertHTML", false, content);
        emitValue();
    };

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography component="label" variant="body2" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                {label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ border: "1px solid", borderColor: "divider", borderBottom: 0, borderRadius: "4px 4px 0 0", px: 0.5, minHeight: 42 }}>
                <Tooltip title="Жирний"><IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => applyInlineStyle({ fontWeight: "700" })} disabled={sourceMode}><FormatBoldIcon /></IconButton></Tooltip>
                <Tooltip title="Курсив"><IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => applyInlineStyle({ fontStyle: "italic" })} disabled={sourceMode}><FormatItalicIcon /></IconButton></Tooltip>
                <Tooltip title="Підкреслений"><IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => applyInlineStyle({ textDecoration: "underline" })} disabled={sourceMode}><FormatUnderlinedIcon /></IconButton></Tooltip>
                <Tooltip title="Колір тексту">
                    <IconButton size="small" component="label" disabled={sourceMode} onMouseDown={rememberSelection}>
                        <FormatColorTextIcon />
                        <input hidden type="color" onChange={(e) => applyInlineStyle({ color: e.target.value })} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Колір виділення">
                    <IconButton size="small" component="label" disabled={sourceMode} onMouseDown={rememberSelection}>
                        <FormatColorFillIcon />
                        <input hidden type="color" defaultValue="#fff59d" onChange={(e) => applyInlineStyle({ backgroundColor: e.target.value })} />
                    </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title={sourceMode ? "Візуальний редактор" : "Редагувати HTML"}>
                    <IconButton size="small" color={sourceMode ? "primary" : "default"} onClick={() => setSourceMode((current) => !current)}><CodeIcon /></IconButton>
                </Tooltip>
            </Stack>
            {sourceMode ? (
                <TextField value={value} onChange={(e) => onChange?.(e.target.value)} fullWidth multiline minRows={Math.max(4, Math.round(minHeight / 24))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "0 0 4px 4px" } }} />
            ) : (
                <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emitValue}
                    onBlur={emitValue}
                    onKeyUp={rememberSelection}
                    onMouseUp={rememberSelection}
                    onPaste={handlePaste}
                    role="textbox"
                    aria-label={label}
                    sx={{ minHeight, maxHeight: 420, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: "0 0 4px 4px", p: 1.5, outline: 0, "&:focus": { borderColor: "primary.main", boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}` }, "& p:first-of-type": { mt: 0 }, "& p:last-child": { mb: 0 } }}
                />
            )}
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
    );
});

export default HtmlEditor;
